/**
 * Date Utility Functions
 * Handles date normalization and formatting for expiry dates
 * Standard Format: MM/YYYY (month/year)
 */

/**
 * Convert Excel serial date to JavaScript Date
 * Excel stores dates as the number of days since January 1, 1900
 * @param {number} serial - Excel serial date number
 * @returns {Date} JavaScript Date object
 */
const excelSerialToDate = (serial) => {
  // Excel's "Day 1" is 1/1/1900 (but Excel wrongly treats 1900 as a leap year)
  const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
  const days = Math.floor(serial);
  const milliseconds = days * 24 * 60 * 60 * 1000;
  return new Date(excelEpoch.getTime() + milliseconds);
};

/**
 * Check if a value is an Excel serial date number
 * Excel serial dates are typically between 1 (1/1/1900) and 60000 (year 2064)
 * @param {any} value - Value to check
 * @returns {boolean}
 */
const isExcelSerialDate = (value) => {
  if (typeof value !== 'number') return false;
  // Valid Excel date range: 1 (1/1/1900) to ~60000 (future dates)
  return value >= 1 && value <= 100000 && Math.floor(value) === value;
};

const parseExcelSerial = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const num = Number(trimmed);
      return Number.isNaN(num) ? null : num;
    }
  }
  return null;
};

/**
 * Parse various date formats and normalize to MM/YYYY
 * Handles:
 * - Excel serial numbers (e.g., 44927)
 * - DD/MM/YYYY (e.g., 15/03/2025)
 * - MM-DD-YYYY (e.g., 03-15-2025)
 * - DD-MM-YYYY (e.g., 15-03-2025)
 * - MM/YYYY (e.g., 03/2025) - already in standard format
 * - MM/YY (e.g., 03/25 or 04/27) - 2-digit year Excel format
 * - YYYY-MM-DD (e.g., 2025-03-15)
 * - Month Year (e.g., "March 2025", "Mar 2025")
 * 
 * @param {string|number} dateValue - Date in various formats
 * @returns {string} Normalized date in MM/YYYY format or empty string if invalid
 */
export const normalizeExpiryDate = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    let date = null;

    // Case 0: Excel serial provided as number or numeric string
    const serialValue = parseExcelSerial(dateValue);
    if (serialValue !== null && isExcelSerialDate(serialValue)) {
      date = excelSerialToDate(serialValue);
    }
    
    // Case 1: Excel serial number
    if (!date && isExcelSerialDate(dateValue)) {
      date = excelSerialToDate(dateValue);
    }
    // Case 2: String date formats
    else if (!date && typeof dateValue === 'string') {
      let trimmed = dateValue.trim();
      
      // Normalize separators: convert dashes and dots to slashes for consistency
      const normalized = trimmed.replace(/[-.\s]/g, '/');

      // If already in MM/YYYY format, return as is (unless year looks like an Excel serial)
      const mmyyyyPattern = /^(0[1-9]|1[0-2])\/\d{4,5}$/;
      if (mmyyyyPattern.test(normalized)) {
        const [monthStr, yearStr] = normalized.split('/');
        const m = parseInt(monthStr, 10);
        const y = parseInt(yearStr, 10);
        if (m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
          return `${String(m).padStart(2, '0')}/${y}`;
        }
        if (m >= 1 && m <= 12 && y > 2100 && y <= 100000) {
          date = excelSerialToDate(y);
        }
      }
      
      // Try MM/YYYY format (e.g., "03/2025")
      if (/^\d{1,2}\/\d{4}$/.test(normalized)) {
        const [month, year] = normalized.split('/');
        const m = parseInt(month, 10);
        const y = parseInt(year, 10);
        if (m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
          return `${String(m).padStart(2, '0')}/${y}`;
        }
      }
      
      // Try MM/YY format (e.g., "03/25" for March 2025, "04/27" for April 2027)
      // 2-digit years: 00-50 → 2000-2050, 51-99 → 1951-1999
      if (/^\d{1,2}\/\d{2}$/.test(normalized)) {
        const [month, yearStr] = normalized.split('/');
        const m = parseInt(month, 10);
        const yy = parseInt(yearStr, 10);
        
        if (m >= 1 && m <= 12) {
          let fullYear;
          if (yy >= 0 && yy <= 50) {
            fullYear = 2000 + yy; // 00-50 → 2000-2050
          } else {
            fullYear = 1900 + yy; // 51-99 → 1951-1999
          }
          return `${String(m).padStart(2, '0')}/${fullYear}`;
        }
      }
      
      // Try DD/MM/YYYY format (e.g., "15/03/2025")
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(normalized)) {
        const parts = normalized.split('/');
        // Assume DD/MM/YYYY if first number > 12 or second number <= 12
        const first = parseInt(parts[0], 10);
        const second = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        let day, month;
        if (first > 12) {
          // Definitely DD/MM/YYYY
          day = first;
          month = second;
        } else if (second > 12) {
          // Definitely MM/DD/YYYY
          month = first;
          day = second;
        } else {
          // Ambiguous - assume DD/MM (more common internationally)
          day = first;
          month = second;
        }
        
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          date = new Date(year, month - 1, day);
        }
      }
      // Try DD-MM-YYYY or MM-DD-YYYY format
      else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(normalized)) {
        const parts = normalized.split('-');
        const first = parseInt(parts[0], 10);
        const second = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        let day, month;
        if (first > 12) {
          day = first;
          month = second;
        } else if (second > 12) {
          month = first;
          day = second;
        } else {
          day = first;
          month = second;
        }
        
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          date = new Date(year, month - 1, day);
        }
      }
      // Try YYYY-MM format (e.g., "2027-04", "2026-06") - commonly used in databases
      else if (/^\d{4}-\d{1,2}$/.test(normalized)) {
        const parts = normalized.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        if (month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
          return `${String(month).padStart(2, '0')}/${year}`;
        }
      }
      // Try YYYY/MM format (slash version)
      else if (/^\d{4}\/\d{1,2}$/.test(normalized)) {
        const parts = normalized.split('/');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        if (month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
          return `${String(month).padStart(2, '0')}/${year}`;
        }
      }
      // Try YYYY-MM-DD format (ISO)
      else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(normalized)) {
        const parts = normalized.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          date = new Date(year, month - 1, day);
        }
      }
      // Try YYYY/MM/DD format (slash version)
      else if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(normalized)) {
        const parts = normalized.split('/');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          date = new Date(year, month - 1, day);
        }
      }
      // Try Month Year format (e.g., "March 2025", "Mar 2025")
      else if (/^[a-zA-Z]+ \d{4}$/.test(trimmed)) {
        const parts = trimmed.split(' ');
        const monthName = parts[0].toLowerCase();
        const year = parseInt(parts[1], 10);
        
        const monthNames = {
          'january': 0, 'jan': 0,
          'february': 1, 'feb': 1,
          'march': 2, 'mar': 2,
          'april': 3, 'apr': 3,
          'may': 4,
          'june': 5, 'jun': 5,
          'july': 6, 'jul': 6,
          'august': 7, 'aug': 7,
          'september': 8, 'sep': 8, 'sept': 8,
          'october': 9, 'oct': 9,
          'november': 10, 'nov': 10,
          'december': 11, 'dec': 11
        };
        
        if (monthNames[monthName] !== undefined) {
          if (year > 2100 && year <= 100000) {
            date = excelSerialToDate(year);
          } else if (year >= 1900 && year <= 2100) {
            date = new Date(year, monthNames[monthName], 1);
          }
        }
      }
      // Try direct Date parsing as last resort
      else {
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
          date = parsed;
        }
      }
    }
    // Case 3: Already a Date object
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    
    // Convert to MM/YYYY format
    if (date && !isNaN(date.getTime())) {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      if (year >= 1900 && year <= 2100) {
        return `${month}/${year}`;
      }
    }
    
    // If all parsing fails, return empty string
    console.warn('[normalizeExpiryDate] Could not parse date:', dateValue);
    return '';
    
  } catch (error) {
    console.error('[normalizeExpiryDate] Error normalizing date:', error, dateValue);
    return '';
  }
};

/**
 * Validate if a date string is in correct MM/YYYY format
 * @param {string} dateStr - Date string to validate
 * @returns {boolean}
 */
export const isValidExpiryFormat = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return false;
  const pattern = /^(0[1-9]|1[0-2])\/\d{4}$/;
  return pattern.test(dateStr.trim());
};

/**
 * Format date for display (MM/YYYY)
 * @param {string|number} dateValue - Date value
 * @returns {string} Formatted date or 'N/A'
 */
export const formatExpiryDateDisplay = (dateValue) => {
  const normalized = normalizeExpiryDate(dateValue);
  return normalized || 'N/A';
};

/**
 * Check if expiry date is in the past
 * @param {string} expiryDate - Date in MM/YYYY format
 * @returns {boolean}
 */
export const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  
  try {
    const normalized = normalizeExpiryDate(expiryDate);
    if (!normalized) return false;
    
    const [month, year] = normalized.split('/').map(Number);
    const expiryDateObj = new Date(year, month - 1, 1); // First day of expiry month
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    return expiryDateObj < currentMonth;
  } catch (error) {
    console.error('[dateUtils] Error checking expiry:', error);
    return false;
  }
};

/**
 * Get expiry status with color coding
 * @param {string} expiryDate - Date in MM/YYYY format
 * @returns {object} Status object with color, label, and isExpired flag
 */
export const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) {
    return {
      color: 'gray',
      label: 'No Date',
      isExpired: false
    };
  }
  
  const normalized = normalizeExpiryDate(expiryDate);
  if (!normalized) {
    return {
      color: 'gray',
      label: 'Invalid',
      isExpired: false
    };
  }
  
  try {
    const [month, year] = normalized.split('/').map(Number);
    const expiryDateObj = new Date(year, month - 1, 1);
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // Calculate months until expiry
    const monthsDiff = (expiryDateObj.getFullYear() - today.getFullYear()) * 12 +
                       (expiryDateObj.getMonth() - today.getMonth());
    
    if (monthsDiff < 0) {
      return {
        color: 'red',
        label: 'Expired',
        isExpired: true
      };
    } else if (monthsDiff <= 3) {
      return {
        color: 'orange',
        label: 'Expiring Soon',
        isExpired: false
      };
    } else {
      return {
        color: 'green',
        label: 'Valid',
        isExpired: false
      };
    }
  } catch (error) {
    console.error('[dateUtils] Error getting expiry status:', error);
    return {
      color: 'gray',
      label: 'Error',
      isExpired: false
    };
  }
};

export default {
  normalizeExpiryDate,
  isValidExpiryFormat,
  formatExpiryDateDisplay,
  isExpired,
  getExpiryStatus,
  excelSerialToDate,
  isExcelSerialDate
};
